import { SectionCard } from "@/components/common/SectionCard";
import { ghostBtnClass, inputClass, primaryBtnClass, rigLabel, techLabel } from "./serviceShared";
import type { useServiceBoardPanel } from "./useServiceBoardPanel";
import { useServiceRouteComposer } from "./useServiceRouteComposer";
import type { ServiceEvent } from "@/lib/service/types";

type Props = {
  d: ReturnType<typeof useServiceBoardPanel>;
  embedded?: boolean;
};

function eventLabel(event: ServiceEvent): string {
  return event.title || event.location_label || event.id.slice(0, 8);
}

export function ServiceRouteComposer({ d, embedded = false }: Props) {
  const r = useServiceRouteComposer(d.events, d.rigs, d.techs, d.routeComposerTick, () => {
    void d.reload();
  });

  const body = (
    <>
      <p className="text-xs text-gray-600 dark:text-zinc-400 mb-3">
        Group unscheduled stops onto a tech + rig + date route. Drag to reorder stops; mixed queue types
        are flagged when rig requirements conflict.
      </p>

      <div className="grid gap-2 sm:grid-cols-4 mb-3">
        <input
          type="date"
          className={inputClass}
          value={r.routeDate}
          onChange={(e) => r.setRouteDate(e.target.value)}
        />
        <select
          className={inputClass}
          value={r.selectedRigId}
          onChange={(e) => {
            r.setSelectedRigId(e.target.value);
            const rig = d.rigs.find((x) => x.id === e.target.value);
            if (rig?.standard_tech_id) r.setSelectedTechId(rig.standard_tech_id);
          }}
        >
          <option value="">Pick rig…</option>
          {d.rigs.map((rig) => (
            <option key={rig.id} value={rig.id}>
              {rig.designation} ({rig.rig_kind})
            </option>
          ))}
        </select>
        <select
          className={inputClass}
          value={r.selectedTechId}
          onChange={(e) => r.setSelectedTechId(e.target.value)}
        >
          <option value="">Tech (optional)</option>
          {d.techs.map((tech) => (
            <option key={tech.id} value={tech.id}>
              {tech.display_name}
            </option>
          ))}
        </select>
        <button
          type="button"
          className={primaryBtnClass}
          disabled={r.actionBusy || !r.routeDate || !r.selectedRigId}
          onClick={() => void r.createRoute()}
        >
          {r.actionBusy ? "Working…" : "New route"}
        </button>
      </div>

      {r.assignWarnings.length > 0 ? (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-950 dark:border-amber-800 dark:bg-amber-950/35 dark:text-amber-100">
          <div className="font-semibold mb-1">Route warnings</div>
          <ul className="list-disc pl-4 space-y-0.5">
            {r.assignWarnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        </div>
      ) : null}

      {r.localError ? (
        <div className="mb-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-900 dark:border-red-800 dark:bg-red-950/35 dark:text-red-100">
          {r.localError}
        </div>
      ) : null}

      {r.routeDate ? (
        <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-500 mb-2">
              Routes on {r.routeDate} {r.listBusy ? "· loading…" : ""}
            </div>
            {r.routes.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-zinc-400">No routes yet — create one above.</p>
            ) : (
              <ul className="space-y-2">
                {r.routes.map((route) => {
                  const selected = r.selectedRouteId === route.id;
                  return (
                    <li
                      key={route.id}
                      className={`rounded-xl border px-3 py-2 ${
                        selected
                          ? "border-indigo-400 bg-indigo-50/60 dark:border-indigo-600 dark:bg-indigo-950/30"
                          : "border-gray-200 dark:border-zinc-700"
                      }`}
                      onDragOver={(e) => {
                        e.preventDefault();
                        e.dataTransfer.dropEffect = "move";
                      }}
                      onDrop={(e) => {
                        e.preventDefault();
                        const eventId = e.dataTransfer.getData("text/service-event-id");
                        if (eventId) void r.assignEvent(route.id, eventId);
                      }}
                    >
                      <button
                        type="button"
                        className="text-left w-full"
                        onClick={() => r.setSelectedRouteId((prev) => (prev === route.id ? null : route.id))}
                      >
                        <div className="text-sm font-medium text-gray-900 dark:text-zinc-100">
                          {route.label || route.id.slice(0, 8)}
                        </div>
                        <div className="text-xs text-gray-600 dark:text-zinc-400">
                          {rigLabel(d.rigs, route.rig_id)} · {techLabel(d.techs, route.tech_id)} ·{" "}
                          {route.stops.length} stop{route.stops.length === 1 ? "" : "s"}
                        </div>
                      </button>
                      {route.warnings.length > 0 ? (
                        <div className="mt-1 text-[10px] text-amber-800 dark:text-amber-200">
                          {route.warnings[0]}
                        </div>
                      ) : null}
                      {selected && route.stops.length > 0 ? (
                        <ol className="mt-2 space-y-1">
                          {route.stops.map((stop, idx) => (
                            <li
                              key={stop.route_event.id}
                              draggable
                              onDragStart={() => r.setDragEventId(stop.event.id)}
                              onDragOver={(e) => e.preventDefault()}
                              onDrop={(e) => {
                                e.preventDefault();
                                r.dropOnStop(route.id, stop.event.id);
                              }}
                              className="flex items-center gap-2 rounded-md border border-gray-200 bg-white px-2 py-1 text-xs dark:border-zinc-600 dark:bg-zinc-800"
                            >
                              <span className="font-mono text-gray-400">{idx + 1}.</span>
                              <span className="flex-1 cursor-grab">{eventLabel(stop.event)}</span>
                              <button
                                type="button"
                                className={ghostBtnClass}
                                disabled={r.actionBusy || idx === 0}
                                onClick={() => r.moveStop(route.id, stop.event.id, -1)}
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className={ghostBtnClass}
                                disabled={r.actionBusy || idx === route.stops.length - 1}
                                onClick={() => r.moveStop(route.id, stop.event.id, 1)}
                              >
                                ↓
                              </button>
                            </li>
                          ))}
                        </ol>
                      ) : selected ? (
                        <div className="mt-2 text-xs italic text-gray-500 dark:text-zinc-500">
                          Drop unscheduled events here
                        </div>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div>
            <div className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-zinc-500 mb-2">
              Available stops
            </div>
            {r.unscheduledEvents.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-zinc-400">No schedulable events for this day.</p>
            ) : (
              <ul className="flex flex-wrap gap-2">
                {r.unscheduledEvents.map((ev) => (
                  <li key={ev.id}>
                    <span
                      draggable
                      onDragStart={(e) => {
                        e.dataTransfer.setData("text/service-event-id", ev.id);
                        e.dataTransfer.effectAllowed = "move";
                      }}
                      className="inline-block cursor-grab rounded-lg border border-indigo-300 bg-indigo-50 px-2 py-1 text-xs font-medium text-indigo-900 active:cursor-grabbing dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-100"
                    >
                      {eventLabel(ev)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      ) : (
        <p className="text-sm text-gray-500 dark:text-zinc-400">Pick a route date to compose multi-stop routes.</p>
      )}
    </>
  );

  if (embedded) return body;
  return <SectionCard title="Route composer">{body}</SectionCard>;
}
